import { createSlice } from '@reduxjs/toolkit';
import navigationService from '../../navigation/navigationService';
import apiClient from '@miroculus/api-client';
import { LOGIN_URL } from '../../constants/routes';
import socketClient from '../../socketClient';
import { redeemInvitation, loadOrganizations } from '../organizations';
import { clearAppStorage } from '../../utils/storage';
const initialState = {
    user: { name: null, email: null },
    logged: false,
    loading: false,
};
const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        updateUser: (state, action) => {
            state.user = { ...state.user, ...action.payload };
        },
        updateLogged: (state, action) => {
            state.logged = action.payload;
        },
        updateLoading: (state, action) => {
            state.loading = action.payload;
        },
    },
});
// Export actions
export const { updateUser, updateLogged, updateLoading } = authSlice.actions;
// Thunk Actions (kept as regular thunks due to cross-module dispatches)
export const loginWithPassword = ({ email, password, inviteToken }) => async (dispatch) => {
    const { body } = await apiClient.post('/auth/signin', { email, password });
    await dispatch(login({ ...body, inviteToken }));
};
export const signUpWithPassword = ({ email, name, password, inviteToken, createToken }) => async (dispatch) => {
    await apiClient.post('/auth/signup', {
        email,
        name,
        password,
        inviteToken,
        createToken,
    });
    await dispatch(loginWithPassword({ email, password }));
};
export const login = ({ jwt, refresh, name, email, inviteToken } = {}) => async (dispatch) => {
    if (jwt && refresh && name && email) {
        apiClient.setUser({ jwt, refresh, name, email });
    }
    const user = apiClient.getUser();
    if (user) {
        dispatch(updateUser(user));
        if (inviteToken) {
            await dispatch(redeemInvitation(inviteToken, email));
        }
        await socketClient.connect();
        await dispatch(loadOrganizations());
        dispatch(updateLoading(false));
        dispatch(updateLogged(true));
    }
    else {
        dispatch(updateLoading(false));
        dispatch(updateLogged(false));
    }
};
export const logout = () => (dispatch) => {
    socketClient.disconnect();
    apiClient.clearUser();
    clearAppStorage();
    dispatch(updateUser({ name: null, email: null }));
    dispatch(updateLogged(false));
    dispatch(updateLoading(false));
    navigationService.push(LOGIN_URL);
};
export const resetPassword = (email) => async () => {
    try {
        await apiClient.post('/auth/password-reset', { email });
    }
    catch (e) {
        console.error(e);
    }
};
export const resetUserPassword = () => (dispatch, getState) => dispatch(resetPassword(getState().auth.user.email || ''));
export const setNewPassword = (password, token) => async () => {
    try {
        await apiClient.post('/auth/password-reset/redeem', { password, token });
    }
    catch (e) {
        console.error(e);
    }
};
export const loadUserData = () => async (dispatch) => {
    dispatch(updateLoading(true));
    try {
        const { body: userAccountData } = await apiClient.get('/users/me');
        dispatch(updateUser({ ...userAccountData }));
    }
    catch (e) {
        console.error(e);
    }
    finally {
        dispatch(updateLoading(false));
    }
};
export const updateAccountSettings = (userData) => async (dispatch) => {
    dispatch(updateLoading(true));
    try {
        await apiClient.put('/users/me', { name: userData.name });
        dispatch(updateUser({ ...userData }));
    }
    catch (e) {
        console.error(e);
    }
    finally {
        dispatch(updateLoading(false));
    }
};
// Export reducer as default
export default authSlice.reducer;
